import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";
import { LANGS } from "../i18n/translations";
import { Logo } from "./Logo";

export const Navbar = () => {
  const { lang, setLang, copy } = useLanguage();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const links = [
    { to: "/", label: copy.nav.home },
    { to: "/sobre-mi", label: copy.nav.about },
    { to: "/servicios", label: copy.nav.services },
    { to: "/blog", label: copy.nav.blog },
    { to: "/contacto", label: copy.nav.contact },
  ];

  return (
    <header className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-linen/80 border-b border-border/60" data-testid="main-navbar">
      <div className="max-w-6xl mx-auto px-5 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5" data-testid="logo-link">
          <Logo className="h-9 w-9" />
          <span className="font-serif font-semibold text-lg tracking-tight text-pine">Gusto <span className="italic text-clay">y</span> Ciencia</span>
        </Link>

        <nav className="hidden md:flex items-center gap-7">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.to === "/" ? "home" : l.to.slice(1)}`}
              className={({ isActive }) =>
                `text-sm font-medium transition-colors duration-300 ${isActive ? "text-clay" : "text-ink/70 hover:text-pine"}`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <div className="flex items-center rounded-full border border-border bg-white/60 p-0.5" data-testid="language-switcher">
            {LANGS.map((l) => (
              <button
                key={l.code}
                data-testid={`language-switcher-${l.code}`}
                onClick={() => setLang(l.code)}
                className={`px-2.5 py-1 text-xs font-mono font-semibold rounded-full transition-colors duration-300 ${
                  lang === l.code ? "bg-pine text-linen" : "text-ink/60 hover:text-pine"
                }`}
              >
                {l.label}
              </button>
            ))}
          </div>
          <button
            data-testid="nav-cta-button"
            onClick={() => navigate("/prenota")}
            className="hidden md:inline-flex items-center rounded-full bg-clay px-4 py-2 text-sm font-semibold text-white transition-colors duration-300 hover:bg-clay-dark"
          >
            {copy.nav.cta}
          </button>
          <button
            data-testid="mobile-menu-button"
            className="md:hidden text-ink"
            onClick={() => setOpen(!open)}
            aria-label="Menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="md:hidden overflow-hidden bg-linen border-b border-border"
            data-testid="mobile-menu"
          >
            <div className="px-5 py-4 flex flex-col gap-1">
              {links.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-link-${l.to === "/" ? "home" : l.to.slice(1)}`}
                  className={({ isActive }) =>
                    `py-2.5 text-base font-medium ${isActive ? "text-clay" : "text-ink/80"}`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
              <button
                data-testid="mobile-nav-cta-button"
                onClick={() => { setOpen(false); navigate("/prenota"); }}
                className="mt-2 rounded-full bg-clay px-4 py-3 text-sm font-semibold text-white"
              >
                {copy.nav.cta}
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
